/**
 * React hooks for breakfast kiosk functionality.
 * These endpoints do NOT require authentication.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiConfig } from '../config';

const API_BASE = apiConfig.baseUrl;

// =============================================================================
// Types
// =============================================================================

export interface BreakfastMenuItem {
  id: number;
  name: string;
  description: string | null;
  category: string;
}

export interface BreakfastMenuResponse {
  date: string;
  items: BreakfastMenuItem[];
  by_category: Record<string, BreakfastMenuItem[]>;
}

export interface EmployeeSearchResult {
  display_name: string;
  employee_number: string | null;
  email?: string | null; // no longer returned (HMH-2397)
  department: string | null;
}

export interface EmployeeSearchResponse {
  results: EmployeeSearchResult[];
  count: number;
}

export interface BreakfastOrderItem {
  menu_item_id: number;
  quantity: number;
  notes?: string;
}

export interface CreateBreakfastOrderRequest {
  employee_name: string;
  employee_number: string | null;
  is_guest: boolean;
  items: BreakfastOrderItem[];
  notes?: string;
  ticket_code?: string;
}

export interface BreakfastOrderResponse {
  id: number;
  employee_name: string;
  employee_number: string | null;
  is_guest: boolean;
  order_status: string;
  items: string[];
  created_at: string;
  message: string;
}

// =============================================================================
// API Functions
// =============================================================================

async function fetchBreakfastMenu(): Promise<BreakfastMenuResponse> {
  // Add cache-busting timestamp to prevent browser caching stale menu data
  const response = await fetch(`${API_BASE}/breakfast/menu?_t=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch breakfast menu');
  return response.json();
}

async function searchEmployees(query: string): Promise<EmployeeSearchResponse> {
  // Signed-in lookup only (admin, kitchen or can_order_for_others), HMH-2397
  const token = sessionStorage.getItem('hmh_access_token');
  const response = await fetch(
    `${API_BASE}/breakfast/search-employee?query=${encodeURIComponent(query)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} }
  );
  if (!response.ok) throw new Error('Failed to search employees');
  return response.json();
}

export type EmployeeLookupResult =
  | { status: 'found'; displayName: string }
  | { status: 'none' }
  | { status: 'limited' }
  | { status: 'error' };

/**
 * Kiosk: the name for one full employee number (HMH-2415). Public and
 * rate-limited; partial or unknown numbers come back as 'none'.
 */
export async function lookupEmployeeByNumber(employeeNumber: string): Promise<EmployeeLookupResult> {
  try {
    const response = await fetch(`${API_BASE}/breakfast/lookup-employee`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ employee_number: employeeNumber }),
    });
    if (response.status === 429) return { status: 'limited' };
    if (!response.ok) return { status: 'error' };
    const data: { match: { display_name: string } | null } = await response.json();
    return data.match ? { status: 'found', displayName: data.match.display_name } : { status: 'none' };
  } catch {
    return { status: 'error' };
  }
}

async function createBreakfastOrder(
  order: CreateBreakfastOrderRequest
): Promise<BreakfastOrderResponse> {
  const response = await fetch(`${API_BASE}/breakfast/order`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(order),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to place order');
  }
  return response.json();
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Fetch breakfast menu items
 */
export function useBreakfastMenu() {
  return useQuery({
    queryKey: ['breakfast', 'menu'],
    queryFn: fetchBreakfastMenu,
    staleTime: 0, // Always fetch fresh data for menu
    gcTime: 60 * 1000, // Keep in cache for 1 minute
  });
}

/**
 * Search for employees by name
 * Debounce the query in the component before calling this
 */
export function useEmployeeSearch(query: string) {
  return useQuery({
    queryKey: ['breakfast', 'employee-search', query],
    queryFn: () => searchEmployees(query),
    enabled: query.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Place a breakfast order
 */
export function usePlaceBreakfastOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createBreakfastOrder,
    onSuccess: () => {
      // Invalidate any order-related queries if needed
      queryClient.invalidateQueries({ queryKey: ['breakfast', 'orders'] });
    },
  });
}
